import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * First-run Dippy welcome wizard state.
 *
 * Mirrors the pattern from `web/src/pages/admin/wizard/useWizardState.ts`
 * (the full Site Wizard). Persisted to localStorage so reloads + back
 * navigation don't lose admin progress. Cleared on successful launch
 * or "Skip for now".
 */

const STORAGE_KEY = 'dippy-welcome-wizard-state';

export type ApprovalPreset =
  | 'full_manual'
  | 'suggest_only'
  | 'auto_with_review'
  | 'fully_autonomous';

export type WelcomeMission = 'discovery' | 'marketplace_sourcing' | 'research' | 'skip';

export interface CategoryEntry {
  name: string;
  priority: 'high' | 'medium' | 'low';
}

export interface WelcomeState {
  currentStep: number;
  completedSteps: number[];
  savedAt: string;

  // Step 2 — autonomy
  approvalPreset: ApprovalPreset | '';

  // Step 3 — brand profile (pre-filled from siteConfig on mount)
  brandDescription: string;
  productType: string;
  categories: CategoryEntry[];
  excludeKeywords: string[];

  // Step 4 — first mission
  mission: WelcomeMission | '';
  researchBrands: string[];
}

interface WelcomeActions {
  updateState: (partial: Partial<WelcomeState>) => void;
  resetState: () => void;
}

const initialState: WelcomeState = {
  currentStep: 0,
  completedSteps: [],
  savedAt: '',
  approvalPreset: '',
  brandDescription: '',
  productType: '',
  categories: [],
  excludeKeywords: [],
  mission: '',
  researchBrands: [],
};

export const useWelcomeState = create<WelcomeState & WelcomeActions>()(
  persist(
    (set) => ({
      ...initialState,
      updateState: (partial) =>
        set((state) => ({
          ...state,
          ...partial,
          savedAt: new Date().toISOString(),
        })),
      resetState: () => {
        set({ ...initialState, savedAt: '' });
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {
          // ignore storage errors
        }
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => {
        const { updateState: _u, resetState: _r, ...data } = state;
        return data;
      },
    }
  )
);
